import * as React from 'react';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../../../settings/interfaces/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../settings/interfaces/ISiteSettingsForSiteAdmin';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ServerSettings } from '../../../ts/resources/ServerSettings';
import { Sites } from '../../../ts/resources/Sites';
import { IGetSiteSettingsForSiteAdminResult } from '../../../ts/ServerApi/requests/GetSiteSettingsForSiteAdminRequest';
import { IEditableServerSettings } from '../../../ts/ServerApi/types/IEditableServerSettings';
import { ServerService } from '../../../ts/services/ServerService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../ts/services/SiteAuthenticationSettingsService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

const template = `<div ng-include="'siteSettings.html'"></div>`;

export interface GeneralSiteSettingsSubspaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly angularContext: AngularContext;
  readonly server: ServerService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface GeneralSiteSettingsSubspaceAngularProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
  readonly siteAuthService: SiteAuthenticationSettingsService;
}

export type GeneralSiteSettingsSubspaceProps = GeneralSiteSettingsSubspaceOwnProps & GeneralSiteSettingsSubspaceAngularProps;

export interface GeneralSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly siteSettings: IGetSiteSettingsForSiteAdminResult;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
}

export class GeneralSiteSettingsSubspaceComponent extends React.PureComponent<GeneralSiteSettingsSubspaceProps, GeneralSiteSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope = undefined;
  private unmounted: boolean = false;

  public constructor(props: GeneralSiteSettingsSubspaceProps) {
    super(props);
    this.state = {hasDataToRender: false, hasError: false, serverSettings: undefined, siteSettings: undefined, siteAuthenticationSettings: undefined, siteSettingsForServerAdmin: undefined};
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!_.isUndefined(this.state.serverSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.siteAuthenticationSettings)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={'tb-stylish-settings'}>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private updateState<K extends keyof GeneralSiteSettingsSubspaceState>(newState: Pick<GeneralSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getServerSettings();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
    this.getSiteAuthenticationSettings();
  }

  private getServerSettings = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        const serverSettings = await this.props.serverSettingsService.fetch();
        this.updateState({serverSettings: serverSettings});
      } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({serverSettings: null});
    }
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForServerAdmin = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        await this.props.server.getSessionInfoPromise();
        const serverSettings = await this.props.sites.getCurrentSiteSettingsForServerAdmin();
        this.updateState({siteSettingsForServerAdmin: serverSettings});
      } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({siteSettingsForServerAdmin: null});
    }
  }

  private getSiteAuthenticationSettings = async () => {
    try {
      const siteAuthSettings = await this.props.siteAuthService.getSiteAuthenticationSettings(/* refresh */ true);
      this.updateState({siteAuthenticationSettings: siteAuthSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISettingsCoordinatorCtrlScope;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;
    childScope.serverSettings = this.state.serverSettings as IServerAdminSettings;
    childScope.siteSettings = this.state.siteSettings as unknown as ISiteSettingsForSiteAdmin;
    childScope.siteSettingsForServerAdmin = this.state.siteSettingsForServerAdmin;
    childScope.siteAuthenticationSettings = this.state.siteAuthenticationSettings;
    this.childScope = childScope;
    return childScope;
  }
}

const GeneralSiteSettingsHOC = angularProviderHOC<GeneralSiteSettingsSubspaceOwnProps, GeneralSiteSettingsSubspaceAngularProps, GeneralSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    serverSettingsService: ServiceLocator.get<ServerSettings>('ServerSettings'),
    sites: ServiceLocator.get<Sites>('Sites'),
    siteAuthService: ServiceLocator.get<SiteAuthenticationSettingsService>('SiteAuthenticationSettingsService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const GeneralSiteSettingsSubspace: React.ComponentType<GeneralSiteSettingsSubspaceOwnProps>
  = GeneralSiteSettingsHOC(GeneralSiteSettingsSubspaceComponent);
